import { archivosFetch, archivosUpload } from '../../utils/fetch';
import {
  MAX_MEGABYTES_PER_FILE,
  MAX_PAGES_PER_DOCUMENT,
} from './constants';
import { DocumentPage, DocumentView, DraftDocument, QrSummary } from './types';

// The archiving wizard's calls, over the backend's draft and page endpoints.
// Shapes verified with curl on 23/08 against the development stack; the
// differences from what was expected are noted where they matter.

const API = `${process.env.REACT_APP_API_URL}/api/archives`;

export const GENERIC_ERROR =
  "L'opération n'a pas pu aboutir. Veuillez réessayer dans quelques instants.";

// The backend answers in English; what the user can understand goes here
// (same pattern as AdminEntities). The keys are the literal messages from
// services/documentPages.js and middlewares/uploadPage.js.
const MESSAGES: Record<string, string> = {
  [`File exceeds the ${MAX_MEGABYTES_PER_FILE} MB limit`]: `Fichier trop volumineux : ${MAX_MEGABYTES_PER_FILE} Mo maximum.`,
  [`A document cannot hold more than ${MAX_PAGES_PER_DOCUMENT} pages`]: `Un document ne peut pas dépasser ${MAX_PAGES_PER_DOCUMENT} pages.`,
  'File is corrupted or does not match its declared type':
    'Le fichier est illisible ou ne correspond pas à son format.',
  'File is empty or corrupted': 'Le fichier est vide ou corrompu.',
  'This PDF carries no page': 'Ce PDF ne contient aucune page.',
  'This QR code belongs to a document that has already been archived':
    'Ce QR code appartient à un document déjà archivé.',
  'This QR code is on a page of one of your drafts. Resume that draft or discard it to use the code here':
    'Ce QR code figure sur un de vos brouillons. Reprenez ce brouillon ou abandonnez-le pour utiliser le code ici.',
  'This QR code is on a page of a document another user is archiving':
    "Ce QR code figure sur un document en cours d'archivage par un autre utilisateur.",
  'This page carries a QR code from another document. Every page must belong to the same one':
    "Cette page porte le QR code d'un autre document. Toutes les pages doivent appartenir au même document.",
  'Several verification QR codes were found on this page. Capture a page carrying a single code':
    "Plusieurs QR codes ont été détectés sur cette page. Capturez une page ne portant qu'un seul code.",
  'This PDF could not be read to look for its QR code':
    "Ce PDF n'a pas pu être lu pour y rechercher un QR code.",
  'This file is already archived in another document':
    'Ce fichier est déjà archivé dans un autre document.',
  'This file is already a page of this document':
    'Ce fichier est déjà une page de ce document.',
  'A document cannot be archived without its issuing_date':
    "La date d'émission est obligatoire.",
  'A document cannot be archived without its issuer':
    "L'émetteur est obligatoire.",
  'Invalid issuing_date': "La date d'émission n'est pas valide.",
  'Not found.': "Ce document n'existe plus.",
  'File not found.': "Ce document n'existe plus.",
};

// Some messages carry the detail inside ("Unsupported file type: text/plain.
// Allowed types are…"); they are recognised by the status.
const BY_STATUS: Record<number, string> = {
  413: `Fichier trop volumineux : ${MAX_MEGABYTES_PER_FILE} Mo maximum.`,
  415: 'Format non pris en charge. Formats autorisés : PDF, PNG, JPEG.',
  403: "Vous n'avez pas les droits pour cette action.",
  404: "Ce document n'existe plus.",
  409: 'Cette page ne peut pas être ajoutée à ce document.',
};

export const translate = (status: number, message?: string): string =>
  (message && MESSAGES[message]) || BY_STATUS[status] || GENERIC_ERROR;

export class ApiError extends Error {
  status: number;

  constructor(status: number, message?: string) {
    super(translate(status, message));
    this.name = 'ApiError';
    this.status = status;
  }
}

const readMessage = async (response: Response): Promise<string | undefined> => {
  try {
    const body = await response.json();
    return typeof body?.message === 'string' ? body.message : undefined;
  } catch (_) {
    return undefined;
  }
};

const failed = async (response: Response): Promise<never> => {
  throw new ApiError(response.status, await readMessage(response));
};

const json = (body: unknown) => ({
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// --- Drafts -------------------------------------------------------------

// `POST /api/archives/` without `image` creates a draft; the archiver comes
// from the token. All fields are optional and the wizard sends none.
export const createDraft = async (): Promise<DraftDocument> => {
  const response = await archivosFetch(`${API}/`, { method: 'POST', ...json({}) });
  if (!response.ok) return failed(response);
  const body = await response.json();
  return body.document as DraftDocument;
};

// Own drafts, from the most recent to the oldest.
export const listDrafts = async (userId: string): Promise<DraftDocument[]> => {
  const params = new URLSearchParams({ status: 'draft', page: '1', limit: '10' });
  const response = await archivosFetch(
    `${API}/${encodeURIComponent(userId)}/my-documents?${params.toString()}`,
  );
  if (!response.ok) return failed(response);
  const body = await response.json();
  return Array.isArray(body?.documents) ? (body.documents as DraftDocument[]) : [];
};

// Each read is recorded in `visits` by the backend: it is the visit
// that the document record must log, and that is why it is not queried more
// than necessary (see pages/Document/useDocumentView).
export const getDocumentView = async (id: string): Promise<DocumentView> => {
  const response = await archivosFetch(
    `${API}/document?id=${encodeURIComponent(id)}`,
  );
  if (!response.ok) return failed(response);
  const body = await response.json();
  return {
    document: body.document as DraftDocument,
    image: typeof body.image === 'string' ? body.image : null,
  };
};

export const getDocument = async (id: string): Promise<DraftDocument> =>
  (await getDocumentView(id)).document;

export type MetadataPayload = {
  id: string;
  title: string;
  /** `YYYY-MM-DD`; the backend stores it as UTC midnight. */
  issuing_date: string;
  service: string;
  issuer: string;
  confidential: boolean;
  verification_url: string;
};

// `archiver` is not sent: the backend ignores it (and pins it to the creator).
export const updateMetadata = async (payload: MetadataPayload): Promise<void> => {
  const response = await archivosFetch(`${API}/`, {
    method: 'PATCH',
    ...json(payload),
  });
  if (!response.ok) return failed(response);
};

// Deletes the document with its pages and files (draft or not).
export const deleteDocument = async (id: string): Promise<void> => {
  const response = await archivosFetch(`${API}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  // A draft that no longer exists is as discarded as one just deleted.
  if (!response.ok && response.status !== 404) return failed(response);
};

// --- Pages --------------------------------------------------------------

export const listPages = async (documentId: string): Promise<DocumentPage[]> => {
  const response = await archivosFetch(
    `${API}/${encodeURIComponent(documentId)}/pages`,
  );
  if (!response.ok) return failed(response);
  const body = await response.json();
  return Array.isArray(body?.pages) ? (body.pages as DocumentPage[]) : [];
};

// The pages an upload became: one, or one per PDF page when the PDF's pages
// carry different codes (each keeps its own).
export const uploadPage = async (
  documentId: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<DocumentPage[]> => {
  const form = new FormData();
  form.append('file', file, file.name);

  const result = await archivosUpload(
    `${API}/${encodeURIComponent(documentId)}/pages`,
    form,
    { onProgress, signal },
  );

  if (!result.ok) {
    const message = (result.body as { message?: unknown } | undefined)?.message;
    throw new ApiError(
      result.status,
      typeof message === 'string' ? message : undefined,
    );
  }

  const body = result.body as { page: DocumentPage; pages?: DocumentPage[] };

  return Array.isArray(body.pages) && body.pages.length > 0 ? body.pages : [body.page];
};

export const deletePage = async (
  documentId: string,
  pageId: string,
): Promise<void> => {
  const response = await archivosFetch(
    `${API}/${encodeURIComponent(documentId)}/pages/${encodeURIComponent(pageId)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) return failed(response);
};

// Requires the full list of ids in their new order.
export const reorderPages = async (
  documentId: string,
  pageIds: string[],
): Promise<DocumentPage[]> => {
  const response = await archivosFetch(
    `${API}/${encodeURIComponent(documentId)}/pages/order`,
    { method: 'PATCH', ...json({ page_ids: pageIds }) },
  );
  if (!response.ok) return failed(response);
  const body = await response.json();
  return Array.isArray(body?.pages) ? (body.pages as DocumentPage[]) : [];
};

export type AssembleResult = {
  id: string;
  status: 'archived';
  page_count: number;
  byte_size: number;
  image_url: string;
  verification_url: string | null;
};

// Assembles the PDF and moves the draft to `archived`. It takes time: with ten
// pages it is seconds, and the caller must show progress.
export const assembleDocument = async (
  documentId: string,
): Promise<AssembleResult> => {
  const response = await archivosFetch(
    `${API}/${encodeURIComponent(documentId)}/pages/assemble`,
    { method: 'POST' },
  );
  if (!response.ok) return failed(response);
  const body = await response.json();
  return body.document as AssembleResult;
};

// --- QR -----------------------------------------------------------------

// Replica of the backend's `qrCodes.summarise`: the page listing does not
// return it, so it is computed here over each page's `verification_url`.
// `mismatch` means the pages carry different codes, which a document may: the
// wizard asks the user to confirm they belong together before archiving.
export const summariseQr = (pages: DocumentPage[]): QrSummary => {
  const found = pages
    .map((page) => page.verification_url)
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
  const distinct = Array.from(new Set(found));

  if (distinct.length === 0) return { status: 'none', url: null };
  if (distinct.length > 1) return { status: 'mismatch', url: null, urls: distinct };
  return { status: 'consistent', url: distinct[0] };
};

// The image paths are relative to the API origin.
export const imageSrc = (path: string): string =>
  `${process.env.REACT_APP_API_URL}${path}`;
